import React, {useContext} from 'react';

import {AnalyticsApiResponse, Dimension, QueryVariables} from 'client/Reports/reports.types';
import {ReportsContext} from 'client/Reports/Reports.context';
import {Badge, WithTooltip} from '@albert-io/atomic';
import {getGuessMetrics, getQuestionMetrics} from 'client/Reports/reports.utils';
import {isEqual} from 'lodash';

import classNames from 'classnames';

import {getStudentAssignmentsView} from './AssignmentSubmitted';

interface Props {
  data: AnalyticsApiResponse;
  dimensions: Dimension[];
  filters: QueryVariables;
}

const getAccuracy = (data: AnalyticsApiResponse) => {
  const accuracy = getGuessMetrics(data)?.values.accuracy;
  if (accuracy !== undefined) {
    return Math.round(accuracy * 100);
  }
  return undefined;
};

const getGrade = (data: AnalyticsApiResponse) => {
  const grade = getQuestionMetrics(data)?.values.grade;
  if (grade !== undefined) {
    return Math.round(grade * 100);
  }
  return undefined;
};

const formatPercent = (value: number) => `${Math.round(value)}%`;

const formatAccuracy = (
  accuracy: number | undefined,
  dimensions: Dimension[],
  filters: QueryVariables
) => {
  if (accuracy === undefined) {
    return 'N/A';
  }
  if (
    (isEqual(dimensions, ['students']) && filters.questionFilter) ||
    (isEqual(dimensions, ['questions']) && filters.studentFilter)
  ) {
    return accuracy === 100 ? 'Correct' : 'Incorrect';
  }
  return formatPercent(accuracy);
};

const formatGrade = (grade: number | undefined) => {
  if (grade === undefined) {
    return 'N/A';
  }

  return formatPercent(grade);
};

const getAssignment = (data: AnalyticsApiResponse) => {
  const studentAssignmentsView = getStudentAssignmentsView(data.dimensions);

  return (
    studentAssignmentsView?.assignment ||
    // in case of gradebook, assignments relationship is not populated, but it should be in the dimensions
    data.dimensions.find((d) => d.type === 'assignments')?.value
  );
};

const isAssignmentUnsubmittedAndNotPastDue = (data: AnalyticsApiResponse) => {
  const studentAssignmentsView = getStudentAssignmentsView(data.dimensions);
  const assignment = getAssignment(data);

  if (!studentAssignmentsView) {
    return false;
  }

  const {submitted} = studentAssignmentsView;
  const dueDate = new Date(assignment.due_date);

  return !submitted && dueDate > new Date();
};

const getMastery = (
  data: AnalyticsApiResponse,
  dimensions: Dimension[],
  filters: QueryVariables,
  performanceCalculateBy: 'accuracy' | 'grade'
) => {
  let value;
  if (performanceCalculateBy === 'grade') {
    value = getGrade(data);
  } else {
    value = getAccuracy(data);
  }

  const formattedValue =
    performanceCalculateBy === 'grade'
      ? formatGrade(value)
      : formatAccuracy(value, dimensions, filters);

  const countAttempts = getGuessMetrics(data)?.values.count_attempts;
  const assignment = getAssignment(data);
  if (!countAttempts && assignment && new Date(assignment.due_date) > new Date()) {
    return {text: 'Not Started', tooltipText: 'Not Started', color: 'u-bgc_slate-50'} as const;
  }
  if (performanceCalculateBy === 'grade' && isAssignmentUnsubmittedAndNotPastDue(data)) {
    return {
      text: formattedValue,
      tooltipText: 'In Progress',
      color: 'u-bgc_slate-100'
    } as const;
  }

  if (value === undefined) {
    return {text: formattedValue, tooltipText: '', color: 'u-bgc_slate-50'} as const;
  }
  if (value < 55) {
    return {text: formattedValue, tooltipText: 'Struggling', color: 'u-bgc_red-500'} as const;
  }
  if (value < 70) {
    return {text: formattedValue, tooltipText: 'Passing', color: 'u-bgc_yellow-600'} as const;
  }
  if (value < 80) {
    return {text: formattedValue, tooltipText: 'Proficient', color: 'u-bgc_green-600'} as const;
  }
  if (value < 90) {
    return {text: formattedValue, tooltipText: 'Excelling', color: 'u-bgc_blue-600'} as const;
  }
  return {text: formattedValue, tooltipText: 'Mastery', color: 'u-bgc_purple-500'} as const;
};

export const Performance = ({data, dimensions, filters}: Props) => {
  const {performanceCalculateBy} = useContext(ReportsContext);
  const showMastery = !!filters.studentFilter || dimensions.includes('students');

  const {text, tooltipText, color} = getMastery(data, dimensions, filters, performanceCalculateBy);

  return (
    <div className='u-text-align_center'>
      <WithTooltip content={tooltipText} placement='left' enabled={showMastery && !!tooltipText}>
        <Badge
          color={color.startsWith('u-bgc_slate') ? 'passive' : 'emphasized'}
          className={classNames('u-white-space_nowrap', {
            [color]: showMastery
          })}
        >
          {text}
        </Badge>
      </WithTooltip>
    </div>
  );
};
